import React, { ChangeEvent } from 'react';
import {
  Box,
  Card,
  Grid,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { withStyles } from '@mui/styles';
import moment from 'moment';

import {
  IEmployeeExamResult,
  IExamSubject
} from '../../interface/employee-exam-result.interface';
import { getEmployeeExamResult } from '../../../../services/employeeService';
import { AppActions } from 'src/store/AppReducer';
import { TableColumnCell } from 'src/components/TableColumnCell';
import { TableHeaderCell } from 'src/components/TableHeaderCell';
import ExamResultViewMore from './exam-result-view-more';

const CustomTableRow = withStyles({
  root: {
    background: '#E6E9FF !important',
    height: '3.5rem'
  }
})(TableRow);

const ExamResults = () => {
  const dispatch = useDispatch();
  const { employeeId } = useParams();
  const [employeeExamResults, setEmployeeExamResults] = React.useState<
    IEmployeeExamResult[]
  >([]);
  const [page, setPage] = React.useState<number>(0);
  const [limit, setLimit] = React.useState<number>(10);
  const [totalCount, setTotalCount] = React.useState<number>(0);
  const [open, setOpen] = React.useState(false);
  const [selectedSubjects, setSelectedSubjects] = React.useState<
    IExamSubject[]
  >([]);

  const handlePageChange = (event: any, newPage: number): void => {
    setPage(newPage);
  };

  const handleLimitChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setPage(0);
    setLimit(parseInt(event.target.value));
  };

  const getExamResultsByEmployeeId = (id: string) => {
    dispatch(AppActions.showLoader());
    getEmployeeExamResult(id, limit, page)
      .then((res: any) => {
        if (res.status === 200) {
          const data = res.data?.data;
          setEmployeeExamResults(data?.employeeAcademicDto ? data.employeeAcademicDto : []);
          setTotalCount(data.dataCount ? data.dataCount : 0);
        }
        dispatch(AppActions.hideLoader());
      })
      .catch((err: any) => {
        dispatch(AppActions.hideLoader());
      });
  };

  const handleDigitNumber = (percentage: string) => {
    const num = parseFloat(percentage);

    if (Number.isInteger(num)) {
      return num.toString();
    } else {
      return num
        .toFixed(1)
        .replace(/(\.\d*?[1-9])0+$/, '$1')
        .replace(/\.$/, '');
    }
  };

  const handleClickOpenViewMore = (selectSubjects: IExamSubject[]) => {
    setSelectedSubjects(selectSubjects);
    setOpen(true);
  };

  const handleCloseViewMore = () => {
    setOpen(false);
    setSelectedSubjects([]);
  };

  React.useEffect(() => {
    getExamResultsByEmployeeId(employeeId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId, page, limit]);

  return (
    <Grid container sx={{ mt: 2 }} justifyContent="space-between">
      <Grid item xs={12}>
        <Card>
          <TableContainer>
            <Table style={{ borderCollapse: 'separate' }} size="small">
              <TableHead>
                <CustomTableRow sx={{ py: 10 }}>
                  <TableHeaderCell content="Exam Name" smallFont />
                  <TableHeaderCell content="Completed Date" smallFont />
                  <TableHeaderCell content="Results" smallFont />
                  <TableHeaderCell content="Percentage" smallFont />
                  <TableHeaderCell content="" />
                </CustomTableRow>
              </TableHead>
              <TableBody>
                {employeeExamResults &&
                  employeeExamResults?.length > 0 &&
                  employeeExamResults.map((listItem, i) => {
                    return (
                      <TableRow hover key={i}>
                        <TableColumnCell>
                          <Typography
                            paddingBottom="0.25rem"
                            paddingTop="0.5rem"
                            style={{ fontSize: '11px' }}
                            variant="body2"
                            gutterBottom
                            noWrap
                          >
                            {listItem.examName || '-'}
                          </Typography>
                        </TableColumnCell>
                        <TableColumnCell>
                          <Typography
                            paddingBottom="0.25rem"
                            paddingTop="0.5rem"
                            style={{ fontSize: '11px' }}
                            variant="body2"
                            gutterBottom
                            noWrap
                          >
                            {listItem.completedDate !== null
                              ? moment(listItem.completedDate).format(
                                  'YYYY/MM/DD'
                                )
                              : '-'}
                          </Typography>
                        </TableColumnCell>
                        <TableColumnCell>
                          <Typography
                            paddingBottom="0.25rem"
                            paddingTop="0.5rem"
                            style={{ fontSize: '11px' }}
                            variant="body2"
                            gutterBottom
                            noWrap
                          >
                            {listItem.result ? listItem.result.charAt(0).toUpperCase() + listItem.result.slice(1) : '-'}
                          </Typography>
                        </TableColumnCell>
                        <TableColumnCell>
                          <Typography
                            paddingBottom="0.25rem"
                            paddingTop="0.5rem"
                            style={{ fontSize: '11px' }}
                            variant="body2"
                            gutterBottom
                            noWrap
                          >
                            {listItem.percentage !== null
                              ? handleDigitNumber(listItem.percentage) + ' %'
                              : '-'}
                          </Typography>
                        </TableColumnCell>
                        <TableColumnCell>
                          <Typography
                            paddingBottom="0.25rem"
                            paddingTop="0.5rem"
                            style={{ fontSize: '11px' }}
                            variant="body2"
                            gutterBottom
                            noWrap
                          >
                            <Typography
                              sx={{
                                fontSize: '11px',
                                color: '#6267FD',
                                textDecorationLine: 'underline',
                                textUnderlineOffset: '2px',
                                cursor: 'pointer'
                              }}
                              onClick={() =>
                                handleClickOpenViewMore(listItem.subjects)
                              }
                            >
                              View More
                            </Typography>
                          </Typography>
                        </TableColumnCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
          {/* view-more */}
          <ExamResultViewMore
            open={open}
            onClose={handleCloseViewMore}
            subjects={selectedSubjects}
          />
          {employeeExamResults?.length === 0 && (
            <Typography
              style={{
                fontSize: '15px',
                fontWeight: 'bold',
                margin: '1rem',
                textAlign: 'center'
              }}
              variant="body2"
            >
              No Records Found
            </Typography>
          )}
          <Box p={2}>
            <TablePagination
              sx={{
                fontSize: '12px',
                '.MuiTablePagination-displayedRows': {
                  fontSize: '12px'
                },
                '.MuiTablePagination-selectLabel': {
                  fontSize: '12px'
                }
              }}
              component="div"
              count={totalCount}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleLimitChange}
              page={page}
              rowsPerPage={limit}
              rowsPerPageOptions={[10, 20, 30, 40, 50]}
            />
          </Box>
        </Card>
      </Grid>
    </Grid>
  );
};

export default ExamResults;
